import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  useTheme
} from '@mui/material';
import { 
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  TrendingUp as TrendingUpIcon,
  Timeline as TimelineIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  Tooltip as ChartTooltip, 
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
  AreaChart,
  Area,
  Tooltip
} from 'recharts';

import AuthLayout from '../components/AuthLayout';
import LoadingIndicator from '../components/LoadingIndicator';
import * as dashboardService from '../services/dashboard.service';
import { COLORS } from '../services/dashboard.service';

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<{
    technicalStats: dashboardService.TechnicalRequestStats[];
    commercialStats: dashboardService.CommercialVisitStats[];
    costData: dashboardService.CostByMonth[];
    forecastData: dashboardService.CostForecast[];
  }>({
    technicalStats: [],
    commercialStats: [],
    costData: [],
    forecastData: []
  });

  // Função para buscar todos os dados do dashboard
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Buscar dados individualmente para lidar com possíveis falhas em endpoints específicos
      let technicalStats: dashboardService.TechnicalRequestStats[] = [];
      let commercialStats: dashboardService.CommercialVisitStats[] = [];
      let costData: dashboardService.CostByMonth[] = [];
      let forecastData: dashboardService.CostForecast[] = [];
      
      try {
        technicalStats = await dashboardService.getTechnicalRequestStats();
        console.log('Estatísticas de chamados técnicos carregadas:', technicalStats);
      } catch (error) {
        console.error('Erro ao buscar estatísticas de chamados técnicos:', error);
      }
      
      try {
        commercialStats = await dashboardService.getCommercialVisitStats();
        console.log('Estatísticas de chamados comerciais carregadas:', commercialStats);
      } catch (error) {
        console.error('Erro ao buscar estatísticas de chamados comerciais:', error);
      }
      
      try {
        costData = await dashboardService.getCostByMonth();
        console.log('Dados de custo por mês carregados:', costData);
      } catch (error) {
        console.error('Erro ao buscar dados de custo por mês:', error);
      }
      
      try {
        forecastData = await dashboardService.getCostForecast();
        console.log('Dados de forecast de custo carregados:', forecastData);
      } catch (error) {
        console.error('Erro ao buscar dados de forecast de custo:', error);
        
        // Se não conseguimos obter dados de forecast mas temos dados de custo,
        // podemos gerar uma previsão simples baseada nos dados de custo
        if (costData.length > 0) {
          console.log('Gerando dados de forecast baseados nos dados de custo existentes');
          forecastData = costData.map(item => ({
            name: item.name,
            actual: item.value,
            forecast: Math.round(item.value * 1.1) // Previsão 10% maior que o valor atual
          } as dashboardService.CostForecast));
          
          // Adicionar previsão para os próximos 3 meses
          const lastMonth = costData[costData.length - 1];
          const months = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
          const lastMonthIndex = months.indexOf(lastMonth.name);
          
          for (let i = 1; i <= 3; i++) {
            const nextMonthIndex = (lastMonthIndex + i) % 12;
            const nextMonthValue = Math.round(lastMonth.value * (1 + (i * 0.05)));
            
            forecastData.push({
              name: months[nextMonthIndex],
              actual: null,
              forecast: nextMonthValue
            } as dashboardService.CostForecast);
          }
        }
      }
      
      setDashboardData({
        technicalStats,
        commercialStats,
        costData,
        forecastData
      });
    } catch (error) {
      console.error('Erro ao buscar dados do dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados ao montar o componente
  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Calcular totais para os cards de resumo
  const calculateTotals = () => {
    const totalTechnicalRequests = dashboardData.technicalStats.reduce((sum, item) => sum + item.value, 0);
    const totalCommercialVisits = dashboardData.commercialStats.reduce((sum, item) => sum + item.value, 0);
    const totalCosts = dashboardData.costData.reduce((sum, item) => sum + item.value, 0);
    
    return {
      technicalRequests: totalTechnicalRequests,
      commercialVisits: totalCommercialVisits,
      costs: totalCosts
    };
  };

  const totals = calculateTotals();

  // Formatar valor monetário
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Função para renderizar uma legenda personalizada
  const renderCustomLegend = (data: any[], title: string) => {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        ml: 2,
        mt: 2
      }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          {title}
        </Typography>
        {data.map((entry, index) => (
          <Box 
            key={`legend-item-${index}`} 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 0.5 
            }}
          >
            <Box 
              sx={{ 
                width: 12, 
                height: 12, 
                backgroundColor: entry.color,
                mr: 1 
              }} 
            />
            <Typography variant="body2">
              {entry.name}: {entry.value}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  // Renderizar o gráfico de chamados técnicos por status
  const renderTechnicalRequestsChart = () => {
    if (!dashboardData.technicalStats || dashboardData.technicalStats.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            Não há dados disponíveis para chamados técnicos.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center' }}>
        <Box sx={{ width: { xs: '100%', md: '70%' }, height: 300 }}>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={dashboardData.technicalStats}
                cx="50%"
                cy="50%"
                labelLine={false} // Desabilitar linhas de conexão para simplificar o gráfico
                outerRadius={100}
                innerRadius={40} // Adicionado para criar um gráfico de donut
                paddingAngle={4} // Espaçamento entre as fatias
                fill="#8884d8"
                dataKey="value"
                nameKey="name"
                isAnimationActive={false} // Desativar animação para evitar problemas
                legendType="none" // Remover legenda gerada automaticamente
                label={({ name, percent, x, y, midAngle }) => {
                  // Não mostrar label quando o percentual for muito pequeno
                  if (percent < 0.05) return null; // 5% ou menos não mostra label
                  
                  // Calcular posição da label
                  const RADIAN = Math.PI / 180;
                  const radius = 80; // Ajustado para ficar dentro do gráfico
                  const sin = Math.sin(-RADIAN * midAngle);
                  const cos = Math.cos(-RADIAN * midAngle);
                  const posX = x + radius * cos;
                  const posY = y + radius * sin;
                  
                  // Retornar apenas o texto, sem fundo
                  return (
                    <text 
                      x={posX} 
                      y={posY} 
                      fill="#fff" // Texto branco para contrastar com o gráfico
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize="12px"
                      fontWeight="bold"
                      style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }} // Sombra para melhorar legibilidade
                    >
                      {(percent * 100).toFixed(0)}%
                    </text>
                  );
                }}
              >
                {dashboardData.technicalStats.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.color} 
                    strokeWidth={1}
                    stroke="#fff"
                  />
                ))}
              </Pie>
              <ChartTooltip 
                formatter={(value, name) => [`${name}: ${value}`, 'Quantidade']}
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  padding: '10px',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>
        <Box sx={{ width: { xs: '100%', md: '30%' }, mt: { xs: 2, md: 0 } }}>
          {renderCustomLegend(dashboardData.technicalStats, 'Status')}
        </Box>
      </Box>
    );
  };

  // Renderizar o gráfico de chamados comerciais por status
  const renderCommercialVisitsChart = () => {
    if (!dashboardData.commercialStats || dashboardData.commercialStats.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            Não há dados disponíveis para chamados comerciais.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center' }}>
        <Box sx={{ width: { xs: '100%', md: '70%' }, height: 300 }}>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={dashboardData.commercialStats}
                cx="50%"
                cy="50%"
                labelLine={false} // Desabilitar linhas de conexão para simplificar o gráfico
                outerRadius={100}
                innerRadius={40} // Adicionado para criar um gráfico de donut
                paddingAngle={4} // Espaçamento entre as fatias
                fill="#8884d8"
                dataKey="value"
                nameKey="name"
                isAnimationActive={false} // Desativar animação para evitar problemas
                legendType="none" // Remover legenda gerada automaticamente
                label={({ name, percent, x, y, midAngle }) => {
                  // Não mostrar label quando o percentual for muito pequeno
                  if (percent < 0.05) return null; // 5% ou menos não mostra label
                  
                  // Calcular posição da label
                  const RADIAN = Math.PI / 180;
                  const radius = 80; // Ajustado para ficar dentro do gráfico
                  const sin = Math.sin(-RADIAN * midAngle);
                  const cos = Math.cos(-RADIAN * midAngle);
                  const posX = x + radius * cos;
                  const posY = y + radius * sin;
                  
                  // Retornar apenas o texto, sem fundo
                  return (
                    <text 
                      x={posX} 
                      y={posY} 
                      fill="#fff" // Texto branco para contrastar com o gráfico
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize="12px"
                      fontWeight="bold"
                      style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }} // Sombra para melhorar legibilidade
                    >
                      {(percent * 100).toFixed(0)}%
                    </text>
                  );
                }}
              >
                {dashboardData.commercialStats.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.color} 
                    strokeWidth={1}
                    stroke="#fff"
                  />
                ))}
              </Pie>
              <ChartTooltip 
                formatter={(value, name) => [`${name}: ${value}`, 'Quantidade']}
                contentStyle={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  padding: '10px',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>
        <Box sx={{ width: { xs: '100%', md: '30%' }, mt: { xs: 2, md: 0 } }}>
          {renderCustomLegend(dashboardData.commercialStats, 'Status')}
        </Box>
      </Box>
    );
  };

  // Renderizar o gráfico de custos por mês
  const renderCostByMonthChart = () => {
    if (!dashboardData.costData || dashboardData.costData.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            Não há dados disponíveis para custos por mês.
          </Typography>
        </Box>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={dashboardData.costData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis 
            tickFormatter={(value) => `R$ ${value / 1000}k`}
          />
          <Tooltip 
            formatter={(value) => [formatCurrency(value as number), 'Custo']}
            contentStyle={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid #ccc',
              borderRadius: '4px',
              padding: '10px',
              boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
            }}
          />
          <Bar dataKey="value" fill={COLORS.info} name="Custo" />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  // Renderizar o gráfico de previsão de custos
  const renderCostForecastChart = () => {
    if (!dashboardData.forecastData || dashboardData.forecastData.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            Não há dados disponíveis para previsão de custos.
          </Typography>
        </Box>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart
          data={dashboardData.forecastData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis 
            tickFormatter={(value) => `R$ ${value / 1000}k`}
          />
          <Tooltip 
            formatter={(value) => [formatCurrency(value as number), 'Valor']}
            contentStyle={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid #ccc',
              borderRadius: '4px',
              padding: '10px',
              boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
            }}
          />
          <Area 
            type="monotone" 
            dataKey="actual" 
            stackId="1" 
            stroke={COLORS.success} 
            fill={COLORS.success} 
            name="Realizado"
            strokeWidth={2}
            fillOpacity={0.3}
          />
          <Area 
            type="monotone" 
            dataKey="forecast" 
            stackId="2" 
            stroke={COLORS.warning} 
            fill={COLORS.warning} 
            name="Previsto"
            strokeWidth={2}
            fillOpacity={0.3}
            strokeDasharray="5 5"
          />
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  return (
    <AuthLayout title="Dashboard Operacional">
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 500 }}>
            Dashboard Operacional
          </Typography>
          <IconButton 
            color="primary" 
            onClick={fetchDashboardData}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : <RefreshIcon />}
          </IconButton>
        </Box>

        {/* Cards de resumo */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" color="text.secondary">
                    Chamados Técnicos
                  </Typography>
                  <PieChartIcon color="primary" />
                </Box>
                <Typography variant="h4" component="div">
                  {totals.technicalRequests}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total de chamados técnicos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" color="text.secondary">
                    Visitas Comerciais
                  </Typography>
                  <BarChartIcon color="secondary" />
                </Box>
                <Typography variant="h4" component="div">
                  {totals.commercialVisits}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total de visitas comerciais
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" color="text.secondary">
                    Custos Totais
                  </Typography>
                  <TrendingUpIcon sx={{ color: COLORS.warning }} />
                </Box>
                <Typography variant="h4" component="div">
                  {formatCurrency(totals.costs)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total de custos acumulados
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" color="text.secondary">
                    Eficiência
                  </Typography>
                  <TimelineIcon sx={{ color: COLORS.success }} />
                </Box>
                <Typography variant="h4" component="div">
                  {totals.technicalRequests > 0 ? 
                    `${Math.round((dashboardData.technicalStats.find(s => s.name === 'Finalizado')?.value || 0) / totals.technicalRequests * 100)}%` : 
                    '0%'
                  }
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Taxa de conclusão de chamados
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Gráficos */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper 
              sx={{ 
                p: 2, 
                display: 'flex', 
                flexDirection: 'column',
                height: '100%',
                minHeight: 400
              }}
            >
              <Typography variant="h6" gutterBottom>
                Chamados Técnicos por Status
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {renderTechnicalRequestsChart()}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper 
              sx={{ 
                p: 2, 
                display: 'flex', 
                flexDirection: 'column',
                height: '100%',
                minHeight: 400
              }}
            >
              <Typography variant="h6" gutterBottom>
                Chamados Comerciais por Status
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {renderCommercialVisitsChart()}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper 
              sx={{ 
                p: 2, 
                display: 'flex', 
                flexDirection: 'column',
                height: '100%',
                minHeight: 400
              }}
            >
              <Typography variant="h6" gutterBottom>
                Custos por Mês
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {renderCostByMonthChart()}
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper 
              sx={{ 
                p: 2, 
                display: 'flex', 
                flexDirection: 'column',
                height: '100%',
                minHeight: 400
              }}
            >
              <Typography variant="h6" gutterBottom>
                Previsão de Custos
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {renderCostForecastChart()}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </AuthLayout>
  );
};

export default Dashboard;
